// A agenda da Central do Perito (fase B): os prazos cadastrados, a entrega do laudo, as tarefas com prazo, os
// recebimentos previstos e os lembretes antes de cada prazo, numa lista só. Portada de `src/lib/agenda.ts` (o
// commit f5c1923 de lá) em 03/11, com o dia contado em São Paulo (`datas.ts`): lá o "hoje" era o dia do
// navegador, e a data do prazo era lida como meia-noite UTC no calendário e como dia nos lembretes.
import { diaDe, hoje, maisDias } from "./datas";
import { etapaAtiva } from "./pipeline";
import type { Pericia, Prazo, Tarefa } from "./tipos";

export type CategoriaEvento = "prazo" | "atividade" | "recebimento" | "lembrete";

export interface EventoAgenda {
  id: string;
  /** AAAA-MM-DD */
  dia: string;
  categoria: CategoriaEvento;
  titulo: string;
  detalhe?: string;
  pericia_id?: string;
  concluido?: boolean;
}

/**
 * O nome e as classes de cada categoria. O par do selo (fundo e texto) é das famílias medidas da casa
 * (`erro-*`, `aviso-*`, `alerta-*`, `chip`); o ponto é token semântico, porque ele precisa aparecer nos dois
 * temas (3 : 1 de forma não textual), e o laranja é `laranja-texto`, nunca o da marca (2,13 : 1 no claro).
 */
export const CATEGORIA_META: Record<CategoriaEvento, { rotulo: string; ponto: string; selo: string }> = {
  prazo: { rotulo: "Prazo", ponto: "bg-erro-tinta", selo: "bg-erro-fundo text-erro-tinta" },
  atividade: { rotulo: "Tarefa", ponto: "bg-acento", selo: "bg-chip text-chip-tinta" },
  recebimento: { rotulo: "Recebimento", ponto: "bg-aviso-tinta", selo: "bg-aviso-fundo text-aviso-tinta" },
  lembrete: { rotulo: "Lembrete", ponto: "bg-laranja-texto", selo: "bg-alerta-fundo text-alerta-tinta" },
};

/** A ordem de urgência entre as categorias: é ela que ordena um mesmo dia. */
export const ORDEM_CATEGORIA: CategoriaEvento[] = ["prazo", "atividade", "recebimento", "lembrete"];

/** Os lembretes antes de cada prazo em aberto: 30, 15, 7, 3 e 1 dia antes (como lá). */
export const DIAS_DE_LEMBRETE = [30, 15, 7, 3, 1] as const;

/**
 * Junta tudo o que tem data. O lembrete de um prazo só nasce para o prazo em ABERTO e só a partir de hoje: o
 * lembrete de ontem não lembra nada.
 */
export function construirAgenda(pericias: Pericia[], prazos: Prazo[], tarefas: Tarefa[], agora: Date = new Date()): EventoAgenda[] {
  const hojeSP = hoje(agora);
  const comLembrete: EventoAgenda[] = [];
  const demais: EventoAgenda[] = [];

  for (const p of prazos) {
    const dia = diaDe(p.data);
    if (!dia) continue;
    comLembrete.push({ id: `prazo:${p.id}`, dia, categoria: "prazo", titulo: p.titulo, detalhe: p.observacao, pericia_id: p.pericia_id, concluido: p.cumprido });
  }

  for (const p of pericias) {
    const diaDoLaudo = diaDe(p.dataEntregaLaudo);
    if (diaDoLaudo) {
      // A entrega do laudo deixa de pesar quando a perícia chega à etapa 10 (o levantamento).
      comLembrete.push({ id: `laudo:${p.id}`, dia: diaDoLaudo, categoria: "prazo", titulo: "Entrega do laudo", detalhe: p.numeroProcesso, pericia_id: p.id, concluido: !etapaAtiva(p) });
    }
    for (const h of p.honorarios) {
      const dia = diaDe(h.dataRecebimento || h.vencimento);
      if (!dia) continue;
      const pago = h.recebido >= h.valor;
      demais.push({ id: `honorario:${h.id}`, dia, categoria: "recebimento", titulo: `${pago ? "Recebido" : "A receber"}: ${h.descricao}`, detalhe: p.numeroProcesso, pericia_id: p.id, concluido: pago });
    }
  }

  for (const t of tarefas) {
    const dia = diaDe(t.prazo);
    if (!dia) continue;
    demais.push({ id: `tarefa:${t.id}`, dia, categoria: "atividade", titulo: t.titulo, detalhe: t.descricao, pericia_id: t.pericia_id, concluido: t.status === "concluida" });
  }

  for (const ev of comLembrete) {
    if (ev.concluido) continue;
    for (const n of DIAS_DE_LEMBRETE) {
      const dia = maisDias(ev.dia, -n);
      if (dia < hojeSP) continue;
      demais.push({ id: `${ev.id}:antes-${n}`, dia, categoria: "lembrete", titulo: `${n === 1 ? "Falta 1 dia" : `Faltam ${n} dias`}: ${ev.titulo}`, detalhe: ev.detalhe, pericia_id: ev.pericia_id });
    }
  }

  return [...comLembrete, ...demais].sort(
    (a, b) => a.dia.localeCompare(b.dia) || ORDEM_CATEGORIA.indexOf(a.categoria) - ORDEM_CATEGORIA.indexOf(b.categoria) || a.id.localeCompare(b.id),
  );
}

/** Os eventos de `de` a `ate`, inclusive (os dois AAAA-MM-DD). */
export function eventosEntre(eventos: EventoAgenda[], de: string, ate: string): EventoAgenda[] {
  return eventos.filter((e) => e.dia >= de && e.dia <= ate);
}

/** A janela de hoje até N dias depois, em São Paulo. */
export function proximosDias(n: number, agora: Date = new Date()): { de: string; ate: string } {
  const de = hoje(agora);
  return { de, ate: maisDias(de, n) };
}

/**
 * As células do mês, de domingo a sábado: `null` antes do dia 1 e depois do último. O mês é "AAAA-MM", e os
 * dias saem como texto, sem `Date` no meio — o dia da semana do dia 1 é o único cálculo, feito em UTC, onde
 * não há fuso para mudar o dia.
 */
export function celulasDoMes(mes: string): Array<string | null> {
  const [a, m] = mes.split("-").map(Number);
  const inicio = new Date(Date.UTC(a, m - 1, 1)).getUTCDay();
  const diasNoMes = new Date(Date.UTC(a, m, 0)).getUTCDate();
  const out: Array<string | null> = Array.from({ length: inicio }, () => null);
  for (let d = 1; d <= diasNoMes; d++) out.push(`${mes}-${String(d).padStart(2, "0")}`);
  while (out.length % 7 !== 0) out.push(null);
  return out;
}

/** O mês N meses depois de "AAAA-MM". */
export function outroMes(mes: string, delta: number): string {
  const [a, m] = mes.split("-").map(Number);
  const d = new Date(Date.UTC(a, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** "novembro de 2026". */
export function nomeDoMes(mes: string): string {
  const [a, m] = mes.split("-").map(Number);
  return `${MESES[m - 1]} de ${a}`;
}

/** "nov" — o mês curto de um dia ou de um mês. */
export function mesCurto(diaOuMes: string): string {
  return MESES_CURTOS[Number(diaOuMes.slice(5, 7)) - 1] ?? "";
}
